import { Component, DestroyRef, inject, OnInit, signal, ViewChild, ViewContainerRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatChipsModule } from '@angular/material/chips';
import { MatSelectModule } from '@angular/material/select';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { ButtonLoadingIndicatorComponent } from '@vendasta/galaxy/button-loading-indicator';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { GalaxySnackbarModule, SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TestManagementService } from '../test-management.service';
import { TestRunTableComponent } from '../test-run-table/test-run-table.component';
import {
  GetTestSuiteResponse,
  RunTestsV2Response,
  ScheduleConfig,
  ScheduleTier,
  TestCase,
  TestCaseKey,
  TestLLMOptions,
  TestSuite,
  TestType,
} from '@vendasta/protos/ai_assistants/v1alpha1';
import {
  TestCaseSelectorModalComponent,
  TestCaseSelectorModalData,
  TestCaseSelectorModalResult,
} from '../test-case-selector-modal/test-case-selector-modal.component';
import {
  TestSuiteCreateEditModalComponent,
  TestSuiteCreateEditModalData,
  TestSuiteCreateEditModalResult,
} from '../test-suite-create-edit-modal/test-suite-create-edit-modal.component';
import {
  TestRunOptionsModalComponent,
  TestRunOptionsModalResult,
} from '../test-run-options-modal/test-run-options-modal.component';
import { NamespaceUtils } from '../../../core/utils/namespace-utils';
import { TranslateService } from '@ngx-translate/core';
import { AiAssistantI18nModule } from '../../../assets/i18n/ai-assistant-i18n.module';
import { catchError, filter, forkJoin, Observable, of } from 'rxjs';

import { isVoiceTest, LLMOptionPair, RunVariant, snackbarDuration } from '../test-run.utils';

interface ScheduleForm {
  tier: FormControl<ScheduleTier>;
  webhookUrl: FormControl<string>;
  consecutiveFailureThreshold: FormControl<number>;
  failureThresholdPercentage: FormControl<number>;
}

const WEBHOOK_URL_MAX_LENGTH = 2048;
const GCHAT_WEBHOOK_HOST = 'chat.googleapis.com';
const THRESHOLD_VALIDATORS = [Validators.min(0), Validators.max(100), Validators.pattern(/^\d+$/)];

// Mirrors the backend's ValidateGChatURL (ai-assistants testsuite/validators.go): empty is
// allowed (notifications disabled), otherwise the value must parse as an https URL whose host
// is exactly chat.googleapis.com. Parsing via URL (not a regex) rejects authority-bypass
// values such as https://chat.googleapis.com@evil.com, whose real hostname is evil.com.
function gchatWebhookUrlValidator(control: AbstractControl): ValidationErrors | null {
  const value = (control.value ?? '').trim();
  if (!value) return null;
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return { gchatWebhook: true };
  }
  return url.protocol === 'https:' && url.hostname === GCHAT_WEBHOOK_HOST ? null : { gchatWebhook: true };
}

interface GroupedTestCase {
  key: string;
  assistantName: string;
  namespaceName: string;
  testCases: TestCase[];
  namespaceType: string;
  namespaceId: string;
  assistantId: string;
}

@Component({
  selector: 'ai-test-suite',
  imports: [
    MatButtonModule,
    MatCardModule,
    MatDividerModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    MatMenuModule,
    MatChipsModule,
    MatSelectModule,
    ReactiveFormsModule,
    GalaxyFormFieldModule,
    ButtonLoadingIndicatorComponent,
    GalaxyPageModule,
    GalaxyLoadingSpinnerModule,
    GalaxySnackbarModule,
    TestRunTableComponent,
    RouterLink,
    AiAssistantI18nModule,
  ],

  templateUrl: './test-suite.component.html',
  styleUrl: './test-suite.component.scss',
})
export class TestSuiteComponent implements OnInit {
  @ViewChild('testRunTable') testRunTable: TestRunTableComponent;

  private readonly testService = inject(TestManagementService);
  private readonly translateService = inject(TranslateService);
  private readonly dialog = inject(MatDialog);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly snackBar = inject(SnackbarService);
  private readonly confirmationModal = inject(OpenConfirmationModalService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);


  testSuiteId: string | null = null;
  testSuite = signal<TestSuite | null>(null);
  testCases = signal<TestCase[]>([]);
  chatTestCases = signal<TestCase[]>([]);
  voiceTestCases = signal<TestCase[]>([]);
  chatGroupedTestCases = signal<GroupedTestCase[]>([]);
  voiceGroupedTestCases = signal<GroupedTestCase[]>([]);
  isLoading = signal<boolean>(true);
  testRunLoading = signal<boolean>(false);
  isSavingSchedule = signal<boolean>(false);
  webhookVisible = signal<boolean>(false);

  protected readonly ScheduleTier = ScheduleTier;
  protected readonly thresholdFields = [
    {
      control: 'consecutiveFailureThreshold',
      labelKey: 'AI_ASSISTANT.TESTING.SCHEDULE_CARD.CONSECUTIVE_LABEL',
      hintKey: 'AI_ASSISTANT.TESTING.SCHEDULE_CARD.CONSECUTIVE_HINT',
    },
    {
      control: 'failureThresholdPercentage',
      labelKey: 'AI_ASSISTANT.TESTING.SCHEDULE_CARD.PERCENTAGE_LABEL',
      hintKey: 'AI_ASSISTANT.TESTING.SCHEDULE_CARD.PERCENTAGE_HINT',
    },
  ] as const;

  scheduleForm = new FormGroup<ScheduleForm>({
    tier: new FormControl<ScheduleTier>(ScheduleTier.SCHEDULE_TIER_NONE, { nonNullable: true }),
    webhookUrl: new FormControl('', {
      nonNullable: true,
      validators: [Validators.maxLength(WEBHOOK_URL_MAX_LENGTH), gchatWebhookUrlValidator],
    }),
    consecutiveFailureThreshold: new FormControl(0, {
      nonNullable: true,
      validators: THRESHOLD_VALIDATORS,
    }),
    failureThresholdPercentage: new FormControl(0, {
      nonNullable: true,
      validators: THRESHOLD_VALIDATORS,
    }),
  });

  ngOnInit(): void {
    this.activatedRoute.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((paramMap) => {
      this.testSuiteId = paramMap.get('testSuiteId');
      if (this.testSuiteId) {
        this.loadTestSuite();
      }
    });
  }

  private setTestSuite(suite: TestSuite | null): void {
    this.testSuite.set(suite);
    this.scheduleForm.reset({
      tier:
        suite?.schedule?.tier && suite.schedule.tier !== ScheduleTier.SCHEDULE_TIER_UNSPECIFIED
          ? suite.schedule.tier
          : ScheduleTier.SCHEDULE_TIER_NONE,
      webhookUrl: suite?.schedule?.notificationGchatWebhookUrl ?? '',
      consecutiveFailureThreshold: suite?.schedule?.consecutiveFailureThreshold ?? 0,
      failureThresholdPercentage: suite?.failureThresholdPercentage ?? 0,
    });
  }

  loadTestSuite(): void {
    if (!this.testSuiteId) return;

    this.isLoading.set(true);
    this.testService.getTestSuite(this.testSuiteId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response: GetTestSuiteResponse) => {
        this.setTestSuite(response.testSuite ?? null);
        const testCases = response.testCases || [];
        this.testCases.set(testCases);
        this.groupTestCases(testCases);
        this.isLoading.set(false);
      },
      error: (err) => {
        console.error('Error loading test suite:', err);
        this.snackBar.openErrorSnack(this.translateService.instant('AI_ASSISTANT.TESTING.SUITE.LOAD_ERROR'), {
          duration: snackbarDuration,
        });
        this.isLoading.set(false);
      },
    });
  }

  // Drops testCaseKeys whose underlying TestCase has been deleted. The backend rejects
  // upserts that reference missing test cases, so without this, a single deleted case
  // permanently blocks every edit on the suite.
  private getResolvedTestCaseKeys(): TestCaseKey[] {
    const suite = this.testSuite();
    if (!suite) return [];
    const resolved = new Set(
      this.testCases()
        .filter((tc) => tc.assistantKey)
        .map((tc) => this.buildKeyString({ assistant: tc.assistantKey, id: tc.id })),
    );
    return (suite.testCaseKeys || []).filter((key) => resolved.has(this.buildKeyString(key)));
  }

  private buildKeyString(key: TestCaseKey): string {
    const nsType = NamespaceUtils.getType(key.assistant?.namespace);
    const nsId = NamespaceUtils.getId(key.assistant?.namespace) || nsType;
    return `${nsType}:${nsId}:${key.assistant?.id}:${key.id}`;
  }

  private groupTestCases(testCases: TestCase[]): void {
    const chatCases = testCases.filter((tc) => !isVoiceTest(tc.testType));
    const voiceCases = testCases.filter((tc) => isVoiceTest(tc.testType));

    this.chatTestCases.set(chatCases);
    this.voiceTestCases.set(voiceCases);
    this.chatGroupedTestCases.set(this.buildGroups(chatCases));
    this.voiceGroupedTestCases.set(this.buildGroups(voiceCases));
  }

  private buildGroups(testCases: TestCase[]): GroupedTestCase[] {
    const groups = new Map<string, GroupedTestCase>();

    for (const tc of testCases) {
      const nsType = NamespaceUtils.getType(tc.assistantKey?.namespace);
      const nsId = NamespaceUtils.getId(tc.assistantKey?.namespace) || nsType;
      const assistantId = tc.assistantKey?.id || 'Unknown';
      const key = `${nsType}:${nsId}:${assistantId}`;

      if (!groups.has(key)) {
        groups.set(key, {
          key,
          assistantName: assistantId.replace('ASSISTANT-', ''),
          namespaceName: `${nsType} - ${nsId}`,
          testCases: [],
          namespaceType: nsType,
          namespaceId: nsId,
          assistantId: assistantId,
        });
      }
      groups.get(key)!.testCases.push(tc);
    }

    return Array.from(groups.values());
  }

  getAssistantTestRoute(group: GroupedTestCase): string[] {
    return ['/ai-assistant', 'testing', group.namespaceType, group.namespaceId, group.assistantId];
  }

  openEditSuiteModal(): void {
    const suite = this.testSuite();
    if (!suite) return;

    const dialogData: TestSuiteCreateEditModalData = {
      testSuite: suite,
      mode: 'edit',
    };

    this.dialog
      .open(TestSuiteCreateEditModalComponent, {
        width: '500px',
        data: dialogData,
      })
      .afterClosed()
      .subscribe((result: TestSuiteCreateEditModalResult) => {
        if (result?.name) {
          this.updateSuite(result);
        }
      });
  }

  // The backend applies the schedule and percentage mutators unconditionally, so any
  // upsert that omits them wipes them server-side; every payload must carry both.
  private buildUpsertPayload(suite: TestSuite, overrides: Partial<TestSuite>): Partial<TestSuite> {
    return {
      ...suite,
      schedule: suite.schedule ? { ...suite.schedule, nextRunAt: undefined } : undefined,
      failureThresholdPercentage: suite.failureThresholdPercentage ?? 0,
      ...overrides,
    };
  }

  saveSchedule(): void {
    const suite = this.testSuite();
    if (!suite || this.scheduleForm.invalid || this.isSavingSchedule()) return;

    const value = this.scheduleForm.getRawValue();
    this.isSavingSchedule.set(true);

    const updatedSuite = this.buildUpsertPayload(suite, {
      schedule: {
        tier: value.tier,
        consecutiveFailureThreshold: value.consecutiveFailureThreshold ?? 0,
        notificationGchatWebhookUrl: value.webhookUrl.trim(),
        nextRunAt: undefined,
      } as ScheduleConfig,
      failureThresholdPercentage: value.failureThresholdPercentage ?? 0,
      testCaseKeys: this.getResolvedTestCaseKeys(),
    });

    this.testService.upsertTestSuite(updatedSuite).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        this.isSavingSchedule.set(false);
        this.setTestSuite(response.testSuite ?? null);
        this.snackBar.openSuccessSnack(this.translateService.instant('AI_ASSISTANT.TESTING.SCHEDULE_CARD.SAVED'));
      },
      error: (err) => {
        this.isSavingSchedule.set(false);
        const message = err.error?.message || this.translateService.instant('AI_ASSISTANT.TESTING.SUITE.UPDATE_ERROR');
        this.snackBar.openErrorSnack(message);
      },
    });
  }

  private updateSuite(result: TestSuiteCreateEditModalResult): void {
    const suite = this.testSuite();
    if (!suite) return;

    const updatedSuite = this.buildUpsertPayload(suite, {
      name: result.name,
      description: result.description,
      tags: result.tags,
      testCaseKeys: this.getResolvedTestCaseKeys(),
    });

    this.testService.upsertTestSuite(updatedSuite).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        this.setTestSuite(response.testSuite ?? null);
        this.snackBar.openSuccessSnack(this.translateService.instant('AI_ASSISTANT.TESTING.SUITE.UPDATED'));
      },
      error: (err) => {
        const message = err.error?.message || this.translateService.instant('AI_ASSISTANT.TESTING.SUITE.UPDATE_ERROR');
        this.snackBar.openErrorSnack(message);
      },
    });
  }

  openAddTestCasesModal(type: 'chat' | 'voice'): void {
    const suite = this.testSuite();
    if (!suite) return;

    const dialogData: TestCaseSelectorModalData = {
      existingTestCaseKeys: suite.testCaseKeys || [],
      testType: type === 'voice' ? TestType.TEST_TYPE_VOICE : TestType.TEST_TYPE_CHAT,
    };

    this.dialog
      .open(TestCaseSelectorModalComponent, {
        width: '600px',
        maxHeight: '80vh',
        data: dialogData,
      })
      .afterClosed()
      .subscribe((result: TestCaseSelectorModalResult) => {
        if (result?.selectedTestCaseKeys?.length > 0) {
          this.addTestCasesToSuite(result.selectedTestCaseKeys);
        }
      });
  }

  private addTestCasesToSuite(newKeys: TestCaseKey[]): void {
    const suite = this.testSuite();
    if (!suite) return;

    const updatedSuite = this.buildUpsertPayload(suite, {
      testCaseKeys: [...this.getResolvedTestCaseKeys(), ...newKeys],
    });

    this.testService.upsertTestSuite(updatedSuite).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.snackBar.openSuccessSnack(
          this.translateService.instant('AI_ASSISTANT.TESTING.SUITE.ADDED_CASES', { count: newKeys.length }),
        );
        this.loadTestSuite();
      },
      error: (err) => {
        const message = err.error?.message || this.translateService.instant('AI_ASSISTANT.TESTING.SUITE.ADD_ERROR');
        this.snackBar.openErrorSnack(message);
      },
    });
  }

  removeTestCase(testCase: TestCase): void {
    const suite = this.testSuite();
    if (!suite) return;

    const updatedKeys = this.getResolvedTestCaseKeys().filter((key) => {
      const keyNsType = NamespaceUtils.getType(key.assistant?.namespace);
      const keyNsId = NamespaceUtils.getId(key.assistant?.namespace);
      const tcNsType = NamespaceUtils.getType(testCase.assistantKey?.namespace);
      const tcNsId = NamespaceUtils.getId(testCase.assistantKey?.namespace);

      return !(
        key.id === testCase.id &&
        key.assistant?.id === testCase.assistantKey?.id &&
        keyNsType === tcNsType &&
        keyNsId === tcNsId
      );
    });

    const updatedSuite = this.buildUpsertPayload(suite, {
      testCaseKeys: updatedKeys,
    });

    this.testService.upsertTestSuite(updatedSuite).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.snackBar.openSuccessSnack(this.translateService.instant('AI_ASSISTANT.TESTING.SUITE.REMOVED'));
        this.loadTestSuite();
      },
      error: (err) => {
        const message = err.error?.message || this.translateService.instant('AI_ASSISTANT.TESTING.SUITE.REMOVE_ERROR');
        this.snackBar.openErrorSnack(message);
      },
    });
  }

  runChatTests(): void {
    const chatKeys = this.getTestCaseKeysByType(false);
    if (chatKeys.length === 0) return;

    this.dialog
      .open(TestRunOptionsModalComponent, {
        width: '800px',
        disableClose: true,
        viewContainerRef: this.viewContainerRef,
        data: {},
      })
      .afterClosed()
      .subscribe((result: TestRunOptionsModalResult) => {
        if (!result) return;

        let llmOptionPairs: LLMOptionPair[] = result.llmOptions.filter(
          (pair) => pair.model !== undefined || pair.reasoningEffort,
        );
        if (llmOptionPairs.length === 0) {
          llmOptionPairs = [{}];
        }

        const runs: RunVariant[] = llmOptionPairs.map((pair) => ({ llmOptions: pair }));
        this.executeTestRuns(chatKeys, runs, result.iterations, result.runNames);
      });
  }

  runVoiceTests(): void {
    const voiceKeys = this.getTestCaseKeysByType(true);
    if (voiceKeys.length === 0) return;

    this.dialog
      .open(TestRunOptionsModalComponent, {
        width: '800px',
        disableClose: true,
        viewContainerRef: this.viewContainerRef,
        data: { voiceOnly: true },
      })
      .afterClosed()
      .subscribe((result: TestRunOptionsModalResult) => {
        if (!result) return;
        const runs: RunVariant[] = result.voiceConfigs.map((voiceConfig) => ({ voiceConfig }));
        this.executeTestRuns(voiceKeys, runs, result.iterations, result.runNames);
      });
  }

  private getTestCaseKeysByType(voice: boolean): TestCaseKey[] {
    const suite = this.testSuite();
    const allTestCases = this.testCases();
    if (!suite || !allTestCases) return [];

    const targetIds = new Set(allTestCases.filter((tc) => isVoiceTest(tc.testType) === voice).map((tc) => tc.id));

    return (suite.testCaseKeys || []).filter((key) => targetIds.has(key.id));
  }

  private executeTestRuns(
    testCaseKeys: TestCaseKey[],
    runs: RunVariant[],
    iterations: number,
    runNames?: string[],
  ): void {
    if (!this.testSuiteId) return;
    this.testRunLoading.set(true);
    const suiteId = this.testSuiteId;

    const requests: Observable<RunTestsV2Response>[] = runs.map((run, index) => {
      const defaultName = this.translateService.instant(
        run.voiceConfig
          ? 'AI_ASSISTANT.TESTING.DEFAULT_VOICE_TEST_RUN_NAME'
          : 'AI_ASSISTANT.TESTING.DEFAULT_TEST_RUN_NAME',
      );
      const testRunName = runNames?.[index] || defaultName;
      const llmOptions: TestLLMOptions | undefined = run.llmOptions
        ? {
            model: run.llmOptions.model ?? '',
            reasoningEffort: run.llmOptions.reasoningEffort ?? '',
          }
        : undefined;
      return this.testService
        .runTestsV2({
          testSuiteId: suiteId,
          testRunName,
          llmOptions,
          voiceConfig: run.voiceConfig,
          testCaseKeys,
          iterations,
        })
        .pipe(catchError(() => of<RunTestsV2Response>({ buildUrl: '' })));
    });

    this.runRequests(requests);
  }

  private runRequests(requests: Observable<RunTestsV2Response>[]): void {
    forkJoin(requests)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
      next: (responses) => {
        const successCount = responses.filter((r) => r.buildUrl).length;
        if (successCount === 0) {
          this.snackBar.openErrorSnack(
            this.translateService.instant('AI_ASSISTANT.TESTING.ERROR_RUNNING_TESTS'),
            { duration: snackbarDuration },
          );
        } else {
          this.snackBar.openSuccessSnack(
            this.translateService.instant('AI_ASSISTANT.TESTING.STARTED_TEST_RUNS', { count: successCount }),
          );
        }
        this.testRunLoading.set(false);
        this.testRunTable?.dataSource?.clearSelection();
      },
      error: (err) => {
        const message = err.error?.message || this.translateService.instant('AI_ASSISTANT.TESTING.ERROR_RUNNING_TESTS');
        this.snackBar.openErrorSnack(message, { duration: snackbarDuration });
        this.testRunLoading.set(false);
      },
    });
  }

  deleteSuite(): void {
    if (!this.testSuiteId) return;

    this.confirmationModal
      .openModal({
        type: 'warn',
        title: this.translateService.instant('AI_ASSISTANT.TESTING.SUITE.DELETE_TITLE'),
        message: this.translateService.instant('AI_ASSISTANT.TESTING.SUITE.DELETE_CONFIRM'),
        confirmButtonText: this.translateService.instant('AI_ASSISTANT.SHARED.DELETE'),
        cancelButtonText: this.translateService.instant('AI_ASSISTANT.SHARED.CANCEL'),
      })
      .pipe(filter((confirmed) => confirmed))
      .subscribe(() => {
        this.testService.deleteTestSuite(this.testSuiteId!).subscribe({
          next: () => {
            this.snackBar.openSuccessSnack(this.translateService.instant('AI_ASSISTANT.TESTING.SUITE.DELETED'));
            this.router.navigate(['../..'], { relativeTo: this.activatedRoute });
          },
          error: (err) => {
            const message =
              err.error?.message || this.translateService.instant('AI_ASSISTANT.TESTING.SUITE.DELETE_ERROR');
            this.snackBar.openErrorSnack(message);
          },
        });
      });
  }
}
